import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Popover, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { CommonTextField } from "src/components/common-textfield";
import { DataRanges } from "src/utils/configs";
import { formatNumber } from "src/utils/util";
import { thunks as regionSvc } from "src/thunks/region";
import { FavoriteRegionStarButton } from "src/components/favorite-region-star-button";
import { RegionScopes } from "src/utils/region-scope";

/**
 * @param dataRanges 표시할 지역 레벨 (예: [DataRanges.SIDO, DataRanges.GUNGU]).
 *   미지정 시 모든 레벨(시도/군구/동) 표시.
 */
export const RegionSearchTextBox = ({
  onRegionSelect,
  width = 120,
  placeholder = "지역검색...",
  dataRanges,
  scope = RegionScopes.DEFAULT,
  query,
  resultsOnly = false,
}) => {
  const dispatch = useDispatch();
  const { sidos, sidosInit, analysisSidos, analysisSidosInit } = useSelector(
    (state) => state.region,
  );
  const targetSidos = scope === RegionScopes.ANALYSIS ? analysisSidos : sidos;
  const targetInit =
    scope === RegionScopes.ANALYSIS ? analysisSidosInit : sidosInit;
  const [localSearchText, setSearchText] = useState("");
  const searchText = query ?? localSearchText;
  const anchorRef = useRef(null);

  // sidos가 없으면 로드
  useEffect(() => {
    if (!targetInit) {
      dispatch(regionSvc.getSidos(scope));
    }
  }, [dispatch, scope, targetInit]);

  // 검색 결과를 useMemo로 계산 (debounce 없이 즉시)
  const searchResults = useMemo(() => {
    if (!targetSidos || !targetSidos.initialized || searchText.trim().length < 2) {
      return [];
    }
    const all = targetSidos.searchRegions(searchText);
    if (!dataRanges || dataRanges.length === 0) return all;
    return all.filter((r) => dataRanges.includes(r.dataRange));
  }, [targetSidos, searchText, dataRanges]);

  const handleSelect = useCallback(
    (region) => {
      if (onRegionSelect) {
        onRegionSelect(region);
      }
      setSearchText("");
    },
    [onRegionSelect]
  );

  const highlightMatches = useCallback((text, matchedKeywords) => {
    if (!matchedKeywords || matchedKeywords.length === 0) {
      return text;
    }

    const parts = [];
    let lastIndex = 0;
    const lowerText = text.toLowerCase();
    const matches = [];

    for (const keyword of matchedKeywords) {
      const lowerKeyword = keyword.toLowerCase();
      let index = lowerText.indexOf(lowerKeyword);
      while (index !== -1) {
        matches.push({ start: index, end: index + keyword.length, keyword });
        index = lowerText.indexOf(lowerKeyword, index + 1);
      }
    }

    matches.sort((a, b) => a.start - b.start);

    for (const match of matches) {
      if (match.start >= lastIndex) {
        if (match.start > lastIndex) {
          parts.push({ text: text.slice(lastIndex, match.start), bold: false });
        }
        parts.push({ text: text.slice(match.start, match.end), bold: true });
        lastIndex = match.end;
      }
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), bold: false });
    }

    return parts.length > 0 ? parts : [{ text, bold: false }];
  }, []);

  // 지역 레벨 표시
  const getRegionType = (dataRange) => {
    if (dataRange === DataRanges.SIDO) return "시도";
    if (dataRange === DataRanges.GUNGU) return "군구";
    return "동";
  };

  const resultsContent = (
    <Stack
      direction="column"
      spacing={1}
      sx={{ minWidth: resultsOnly ? 0 : 300, p: 1 }}
    >
      {searchResults.length > 0 && (
        <Box
          sx={{
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          {searchResults.map((region) => {
            const parts = highlightMatches(region.fullName, region.matchedKeywords);
            const regionType = getRegionType(region.dataRange);

            return (
              <Box
                key={`${region.dataRange}-${region.id}`}
                onClick={() => handleSelect(region)}
                sx={{
                  p: 1,
                  cursor: "pointer",
                  borderBottom: "1px solid #eee",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      backgroundColor:
                        region.dataRange === DataRanges.SIDO
                          ? "#e3f2fd"
                          : region.dataRange === DataRanges.GUNGU
                            ? "#f3e5f5"
                            : "#e8f5e9",
                      px: 0.5,
                      borderRadius: 0.5,
                    }}
                  >
                    {regionType}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                    {Array.isArray(parts)
                      ? parts.map((part, i) =>
                          part.bold ? (
                            <strong key={i}>{part.text}</strong>
                          ) : (
                            <span key={i}>{part.text}</span>
                          )
                        )
                      : region.fullName}
                  </Typography>
                  <FavoriteRegionStarButton
                    regionId={region.id}
                    regionName={region.fullName}
                    dataRange={region.dataRange}
                  />
                </Stack>
                {(region.dataRange === DataRanges.SIDO ||
                  region.dataRange === DataRanges.GUNGU) &&
                  region.population && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      인구 {formatNumber(region.population)}명
                    </Typography>
                  )}
              </Box>
            );
          })}
        </Box>
      )}
      {searchResults.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
        >
          검색 결과가 없습니다.
        </Typography>
      )}
    </Stack>
  );

  if (resultsOnly) return resultsContent;

  return (
    <>
      <Box
        ref={anchorRef}
        sx={width === "100%" ? { width: "100%", minWidth: 0 } : undefined}
      >
        <CommonTextField
          text={searchText}
          placeholder={placeholder}
          onTextChanged={setSearchText}
          width={width}
        />
      </Box>
      <Popover
        open={searchText.length >= 2}
        anchorEl={anchorRef.current}
        onClose={() => setSearchText("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableAutoFocus
        disableEnforceFocus
      >
        {searchText.length >= 2 && resultsContent}
      </Popover>
    </>
  );
};
